import type {DownloadFinishedEvent} from 'bitmovin-player';

import {EventDispatcher, Subscribable} from '../../../core/EventDispatcher';
import {HttpRequestType} from '../../../features/httprequesttracking/HttpRequestType';
import {OnDownloadFinishedEventObject} from '../../../features/httprequesttracking/OnDownloadFinishedEventObject';
import {objectValues} from '../../../utils/objectUtils';
import {calculateTimeWithUndefined, getCurrentTimestamp} from '../../../utils/Utils';
import {Bitmovin8PlayerProxy} from '../Bitmovin8PlayerProxy';

import {BITMOVIN8_PLAYER_EVENT} from './Bitmovin8PlayerTypes';

export class HttpRequestTrackingAdapter implements Subscribable<OnDownloadFinishedEventObject> {
  private eventDispatcher = new EventDispatcher<OnDownloadFinishedEventObject>();

  constructor(player: Bitmovin8PlayerProxy) {
    player.on(BITMOVIN8_PLAYER_EVENT.DownloadFinished, (event) =>
      this.onDownloadFinished(event as DownloadFinishedEvent),
    );
  }

  subscribe(callback: (args: OnDownloadFinishedEventObject) => void): () => void {
    return this.eventDispatcher.subscribe(callback);
  }

  unsubscribe(callback: (args: OnDownloadFinishedEventObject) => void) {
    this.eventDispatcher.unsubscribe(callback);
  }

  private onDownloadFinished(event?: DownloadFinishedEvent) {
    this.eventDispatcher.dispatch({
      httpRequest: {
        downloadTime: calculateTimeWithUndefined(event?.downloadTime) ?? 0,
        httpStatus: event?.httpStatus ?? 0,
        success: event?.success ?? false,
        timestamp: getCurrentTimestamp(),
        url: event?.url,
        size: event?.size,
        timeToFirstByte: calculateTimeWithUndefined(event?.timeToFirstByte),
        type: mapHttpRequestType(event?.downloadType),
      },
    });
  }
}

/**
 * Map Bitmovin8 player `HttpRequestType` to the analytics {@link HttpRequestType} enum, defaulting to `UNKNOWN` for unmapped values.
 */
export function mapHttpRequestType(httpRequestTypeValue: string | undefined): HttpRequestType {
  if (objectValues(HttpRequestType).includes(httpRequestTypeValue)) {
    return httpRequestTypeValue as HttpRequestType;
  }
  // For unmapped values (including undefined), return UNKNOWN. This ensures that the analytics system receives a valid
  // enum value and can handle it appropriately.
  // The unmapped value itself should be detected by unit tests, once the bitmovin player dependency is updated in the project.
  return HttpRequestType.UNKNOWN;
}
